import { TooltipContent } from "@/components/ui/tooltip";
import { MinusIcon, PlusIcon, XIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { TooltipProvider } from "@/components/ui/tooltip";
import { CornerLeftUpIcon } from "lucide-react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useProjectStoreContext } from "./ProjectStoreContext";
import { useRowSelectionCount } from "@/components/data-table/hooks/useRowSelectionCount";
import { useProjectStoreFormStore } from "../hooks/useProjectStoreFormStore";

const PeriodsFooter = () => {
  const { t } = useTranslation();
  const { clearAndRemoveSelectedStores, updatePeriodsInSelectedStores } =
    useProjectStoreContext();

  const isSubmitting = useProjectStoreFormStore((s) => s.isSubmitting);

  // Use selection count from context
  const selectedCount = useRowSelectionCount();
  const isSomeRowsSelected = selectedCount > 0;

  const onAddPeriodsToSelectedStore = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      updatePeriodsInSelectedStores(true);
    },
    [updatePeriodsInSelectedStores],
  );

  const onRemovePeriodsToSelectedStore = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      updatePeriodsInSelectedStores(false);
    },
    [updatePeriodsInSelectedStores],
  );

  const onClearAndRemoveSelectedStores = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      clearAndRemoveSelectedStores();
    },
    [clearAndRemoveSelectedStores],
  );

  const disabled = !isSomeRowsSelected || isSubmitting;

  return (
    <div className="flex items-center">
      <div className="w-[50px] flex justify-center">
        <CornerLeftUpIcon className="w-4 h-4 text-slate-400" />
      </div>
      <div className="flex gap-2">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                onClick={onAddPeriodsToSelectedStore}
                disabled={disabled}
                className={"!pointer-events-auto"}
                data-testid="add-selected-periods-button"
              >
                <PlusIcon className="w-4 h-4" />
                <span className="hidden @3xl:block">
                  {t("projects.projectStores.addSelectedPeriod")}
                </span>
              </Button>
            </TooltipTrigger>
            <TooltipContent className="block @3xl:hidden" align="start">
              {t("projects.projectStores.addSelectedPeriod")}
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                onClick={onRemovePeriodsToSelectedStore}
                disabled={disabled}
                className={"!pointer-events-auto"} // Prevent the tooltip from being disabled
                data-testid="remove-selected-periods-button"
              >
                <MinusIcon className="w-4 h-4" />
                <span className="hidden @3xl:block">
                  {t("projects.projectStores.removeSelectedPeriod")}
                </span>
              </Button>
            </TooltipTrigger>
            <TooltipContent className="block @3xl:hidden">
              {t("projects.projectStores.removeSelectedPeriod")}
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                onClick={onClearAndRemoveSelectedStores}
                disabled={disabled}
                className={"!pointer-events-auto"} // Prevent the tooltip from being disabled
                data-testid="mark-for-removal-button"
              >
                <XIcon className="w-4 h-4" />
                <span className="hidden @3xl:block">
                  {t("projects.projectStores.markForRemoval")}
                </span>
              </Button>
            </TooltipTrigger>
            <TooltipContent className="block @3xl:hidden">
              {t("projects.projectStores.markForRemoval")}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
};

export default PeriodsFooter;
